import type { Note, NoteBlock } from '../types/notes'
import { blockText, toMarkdown, toPlainText } from './notes'

export type ExportFormat = 'pdf' | 'docx' | 'txt' | 'md' | 'image' | 'cznote'

function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function safeName(title: string) {
  return (title.trim() || 'note').replace(/[^\w\d\-\s]/g, '').slice(0, 60) || 'note'
}

/** Only data URLs can be embedded — remote images are referenced, not inlined. */
function embeddableImages(note: Note) {
  return note.attachments.filter(
    (attachment) =>
      (attachment.kind === 'image' || attachment.kind === 'drawing') &&
      attachment.previewUrl?.startsWith('data:image'),
  )
}

/* ------------------------------------------------------------------ PDF ---- */

export async function exportPdf(note: Note) {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const margin = 56
  const width = doc.internal.pageSize.getWidth() - margin * 2
  const bottom = doc.internal.pageSize.getHeight() - margin
  let y = margin

  function ensure(space: number) {
    if (y + space > bottom) {
      doc.addPage()
      y = margin
    }
  }

  function write(text: string, size: number, style: 'normal' | 'bold' | 'italic', indent = 0) {
    if (!text) return
    doc.setFont('helvetica', style)
    doc.setFontSize(size)
    const lines = doc.splitTextToSize(text, width - indent) as string[]
    lines.forEach((line) => {
      ensure(size * 1.5)
      doc.text(line, margin + indent, y)
      y += size * 1.45
    })
  }

  write(note.title || 'Untitled note', 20, 'bold')
  y += 4
  doc.setTextColor(120)
  write(
    [note.diaryDate ?? note.createdAt, note.mood ? `Mood: ${note.mood}` : null, `Edited ${note.updatedAt}`]
      .filter(Boolean)
      .join('  ·  '),
    9,
    'normal',
  )
  doc.setTextColor(20)
  y += 10

  note.blocks.forEach((block) => {
    switch (block.kind) {
      case 'heading': {
        const size = block.level === 1 ? 16 : block.level === 3 ? 12 : 14
        y += 8
        write(blockText(block), size, 'bold')
        y += 2
        break
      }
      case 'bulleted':
        write(`•  ${blockText(block)}`, 11, 'normal', 12)
        break
      case 'numbered':
        write(`${numberFor(note.blocks, block)}.  ${blockText(block)}`, 11, 'normal', 12)
        break
      case 'checklist':
        write(`${block.checked ? '[x]' : '[  ]'}  ${blockText(block)}`, 11, 'normal', 12)
        break
      case 'quote':
        write(blockText(block), 11, 'italic', 18)
        break
      case 'separator':
        ensure(18)
        y += 6
        doc.setDrawColor(200)
        doc.line(margin, y, margin + width, y)
        y += 12
        break
      case 'table': {
        const rows = block.rows ?? []
        const columns = rows[0]?.length ?? 0
        if (!columns) break
        const cellWidth = width / columns
        y += 6
        rows.forEach((row, rowIndex) => {
          ensure(22)
          doc.setFont('helvetica', rowIndex === 0 ? 'bold' : 'normal')
          doc.setFontSize(10)
          row.forEach((cell, columnIndex) => {
            const clipped = doc.splitTextToSize(cell, cellWidth - 10)[0] ?? ''
            doc.text(clipped, margin + columnIndex * cellWidth + 5, y + 13)
          })
          doc.setDrawColor(215)
          doc.rect(margin, y, width, 20)
          for (let index = 1; index < columns; index += 1) {
            doc.line(margin + index * cellWidth, y, margin + index * cellWidth, y + 20)
          }
          y += 20
        })
        y += 10
        break
      }
      default:
        write(blockText(block), 11, 'normal')
        y += 2
    }
  })

  const images = embeddableImages(note)
  if (images.length > 0) {
    y += 12
    write('Attached images and drawings', 12, 'bold')
    images.forEach((image) => {
      const height = 200
      ensure(height + 24)
      try {
        doc.addImage(image.previewUrl!, 'PNG', margin, y, Math.min(width, 320), height, undefined, 'FAST')
        y += height + 6
      } catch {
        // An unreadable image is skipped rather than corrupting the document.
      }
      write(image.name, 9, 'italic')
      y += 6
    })
  }

  const referenced = note.attachments.filter((item) => !embeddableImages(note).includes(item))
  if (referenced.length > 0) {
    y += 10
    write('Attachments', 12, 'bold')
    referenced.forEach((item) => {
      write(`•  ${item.name} (${item.size})${item.transcript ? ' — transcribed' : ''}`, 10, 'normal', 12)
    })
  }

  doc.save(`${safeName(note.title)}.pdf`)
}

function numberFor(blocks: NoteBlock[], block: NoteBlock) {
  let count = 0
  for (const item of blocks) {
    if (item.kind === 'numbered') count += 1
    else if (item.id !== block.id) count = 0
    if (item.id === block.id) break
  }
  return count || 1
}

/* ----------------------------------------------------------------- DOCX ---- */

export async function exportDocx(note: Note) {
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    ImageRun,
  } = await import('docx')

  const children: unknown[] = [
    new Paragraph({ text: note.title || 'Untitled note', heading: HeadingLevel.TITLE }),
    new Paragraph({
      children: [
        new TextRun({
          text: [note.diaryDate ?? note.createdAt, note.mood ? `Mood: ${note.mood}` : null]
            .filter(Boolean)
            .join('  ·  '),
          italics: true,
          color: '6B7280',
          size: 18,
        }),
      ],
    }),
  ]

  note.blocks.forEach((block) => {
    const text = blockText(block)
    switch (block.kind) {
      case 'heading':
        children.push(
          new Paragraph({
            text,
            heading:
              block.level === 1
                ? HeadingLevel.HEADING_1
                : block.level === 3
                  ? HeadingLevel.HEADING_3
                  : HeadingLevel.HEADING_2,
          }),
        )
        break
      case 'bulleted':
        children.push(new Paragraph({ text, bullet: { level: 0 } }))
        break
      case 'numbered':
        children.push(new Paragraph({ text: `${numberFor(note.blocks, block)}. ${text}` }))
        break
      case 'checklist':
        children.push(new Paragraph({ text: `${block.checked ? '☑' : '☐'} ${text}` }))
        break
      case 'quote':
        children.push(
          new Paragraph({ children: [new TextRun({ text, italics: true })], indent: { left: 360 } }),
        )
        break
      case 'separator':
        children.push(
          new Paragraph({ children: [new TextRun({ text: '————————————————', color: 'D1D5DB' })] }),
        )
        break
      case 'table': {
        const rows = block.rows ?? []
        if (rows.length === 0) break
        children.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: rows.map(
              (row, rowIndex) =>
                new TableRow({
                  children: row.map(
                    (cell) =>
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [new TextRun({ text: cell, bold: rowIndex === 0 })],
                          }),
                        ],
                      }),
                  ),
                }),
            ),
          }) as never,
        )
        children.push(new Paragraph({ text: '' }))
        break
      }
      default:
        children.push(new Paragraph({ text }))
    }
  })

  const images = embeddableImages(note)
  if (images.length > 0) {
    children.push(new Paragraph({ text: 'Attached images and drawings', heading: HeadingLevel.HEADING_2 }))
    for (const image of images) {
      try {
        const response = await fetch(image.previewUrl!)
        const buffer = await response.arrayBuffer()
        children.push(
          new Paragraph({
            children: [new ImageRun({ data: buffer, transformation: { width: 420, height: 260 } })],
          }),
        )
        children.push(new Paragraph({ children: [new TextRun({ text: image.name, italics: true, size: 18 })] }))
      } catch {
        children.push(new Paragraph({ text: `${image.name} (could not be embedded)` }))
      }
    }
  }

  const rest = note.attachments.filter((item) => !images.includes(item))
  if (rest.length > 0) {
    children.push(new Paragraph({ text: 'Attachments', heading: HeadingLevel.HEADING_2 }))
    rest.forEach((item) => {
      children.push(new Paragraph({ text: `${item.name} (${item.size})`, bullet: { level: 0 } }))
      if (item.transcript) {
        children.push(
          new Paragraph({ children: [new TextRun({ text: item.transcript, italics: true })], indent: { left: 360 } }),
        )
      }
    })
  }

  const doc = new Document({ sections: [{ children: children as never[] }] })
  const blob = await Packer.toBlob(doc)
  save(blob, `${safeName(note.title)}.docx`)
}

/* ---------------------------------------------------------------- Image ---- */

export async function exportImage(note: Note) {
  const scale = 2
  const width = 760
  const padding = 48
  const measure = document.createElement('canvas').getContext('2d')!

  type Line = { text: string; size: number; weight: string; italic?: boolean; indent: number; gap: number; rule?: boolean }
  const lines: Line[] = []

  function push(text: string, size: number, weight: string, indent = 0, italic = false, gap = 10) {
    measure.font = `${italic ? 'italic ' : ''}${weight} ${size}px Inter, sans-serif`
    const words = text.split(' ')
    let current = ''
    const maxWidth = width - padding * 2 - indent
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word
      if (measure.measureText(candidate).width > maxWidth && current) {
        lines.push({ text: current, size, weight, italic, indent, gap: size * 0.5 })
        current = word
      } else {
        current = candidate
      }
    })
    lines.push({ text: current, size, weight, italic, indent, gap })
  }

  push(note.title || 'Untitled note', 30, '600', 0, false, 6)
  push(
    [note.diaryDate ?? note.createdAt, note.mood ? `Mood: ${note.mood}` : null].filter(Boolean).join('  ·  '),
    13,
    '400',
    0,
    true,
    22,
  )

  note.blocks.forEach((block) => {
    const text = blockText(block)
    switch (block.kind) {
      case 'heading':
        push(text, block.level === 1 ? 22 : block.level === 3 ? 16 : 19, '600', 0, false, 12)
        break
      case 'bulleted':
        push(`•  ${text}`, 15, '400', 16)
        break
      case 'numbered':
        push(`${numberFor(note.blocks, block)}.  ${text}`, 15, '400', 16)
        break
      case 'checklist':
        push(`${block.checked ? '☑' : '☐'}  ${text}`, 15, '400', 16)
        break
      case 'quote':
        push(text, 15, '400', 24, true)
        break
      case 'separator':
        lines.push({ text: '', size: 1, weight: '400', indent: 0, gap: 24, rule: true })
        break
      case 'table':
        (block.rows ?? []).forEach((row, index) => push(row.join('   |   '), 14, index === 0 ? '600' : '400', 8))
        break
      default:
        push(text, 15, '400')
    }
  })

  const height = lines.reduce((total, line) => total + line.size * 1.35 + line.gap, padding * 2)
  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = Math.max(height, 320) * scale
  const context = canvas.getContext('2d')!
  context.scale(scale, scale)
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, canvas.height / scale)

  let y = padding
  lines.forEach((line) => {
    y += line.size * 1.05
    if (line.rule) {
      context.strokeStyle = '#e3e8ee'
      context.beginPath()
      context.moveTo(padding, y)
      context.lineTo(width - padding, y)
      context.stroke()
    } else {
      context.fillStyle = line.italic || line.size <= 13 ? '#5b6672' : '#0f172a'
      context.font = `${line.italic ? 'italic ' : ''}${line.weight} ${line.size}px Inter, sans-serif`
      context.fillText(line.text, padding + line.indent, y)
    }
    y += line.gap
  })

  await new Promise<void>((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) save(blob, `${safeName(note.title)}.png`)
      resolve()
    }, 'image/png')
  })
}

/* -------------------------------------------------- Campuzon Note format ---- */

/**
 * The lossless format: content, metadata, relations, attachments and sharing —
 * everything needed to reimport the note into another Campuzon account.
 */
export function exportCampuzonNote(note: Note) {
  const payload = {
    format: 'campuzon-note',
    version: 1,
    exportedOn: new Date().toISOString(),
    note: {
      id: note.id,
      title: note.title,
      contentFormat: note.contentFormat,
      blocks: note.blocks,
      folderId: note.folderId,
      tags: note.tags,
      attachments: note.attachments,
      academicLinks: note.academicLinks,
      institution: note.institution,
      academicSession: note.academicSession,
      semester: note.semester,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
      reminder: note.reminder,
      diaryDate: note.diaryDate,
      mood: note.mood,
      isPrivate: note.isPrivate,
      locked: note.locked,
      lifecycle: note.lifecycle,
      pinned: note.pinned,
      shares: note.shares.map((share) => ({ handle: share.handle, role: share.role })),
      versions: note.versions,
    },
  }
  save(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    `${safeName(note.title)}.cznote.json`,
  )
}

/* ------------------------------------------------------------ Text based ---- */

export function exportText(note: Note, format: 'md' | 'txt') {
  const body = format === 'md' ? toMarkdown(note) : toPlainText(note)
  save(
    new Blob([body], { type: 'text/plain;charset=utf-8' }),
    `${safeName(note.title)}.${format === 'md' ? 'md' : 'txt'}`,
  )
}

export async function runExport(note: Note, format: ExportFormat) {
  if (format === 'pdf') return exportPdf(note)
  if (format === 'docx') return exportDocx(note)
  if (format === 'image') return exportImage(note)
  if (format === 'cznote') return exportCampuzonNote(note)
  return exportText(note, format)
}
